import { IImageProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPImage, IPPrimaryButton, IPText } from 'baseComponents';
import { IPImageFit } from 'baseComponents/Image/IPImage';
import { IPStack } from 'baseComponents/Stack/IPStack';
import CopilotDashboardDeepLinkActivator from 'components/Copilot/Panel/CopilotDashboardDeepLinkActivator';
import DashboardCollection from 'components/Dashboard/DashboardCollection';
import { LearnMore } from 'components/Notification/Info';
import WarningMessage from 'components/Notification/WarningMessage';
import SceneBar from 'components/SceneBar/SceneBar';
import { getCurrentPivotKeyFromURL, IScenePivotItemProps } from 'components/SceneBar/ScenePivot';
import { globalKB_AdminSubscription, globalKB_AdminUsers, globalNotFoundURL } from 'globalConstants';
import {
  globalPaddingScene,
  globalStackItemStylesPaddingScene,
  globalStackStylesHeight100,
  globalStackStylesPaddingScene,
  globalStackTokensGapLarge,
  globalStackTokensGapSmall,
} from 'globalStyles';
import { DashboardTypes } from 'models/dashboard';
import DashboardScene from 'models/dashboardScene';
import { isSameDirectory } from 'models/identityProvider';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { PartnerDashboardContent } from 'scenes/PartnerAdmin/PartnerDashboard';
import { apiGetDashboardScene } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature, hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import Config from 'services/Config/configService';
import { isAm } from 'utils/datetime';
import { SubscriptionTypes } from 'utils/subscription';
import TrialWelcome from './Trial/TrialWelcome';

interface IDashboardOverviewProps extends RouteComponentProps {}

const imagePropsLicenseManager: IImageProps = {
  src: `${Config.getImageURL()}/license-manager.png`,
  imageFit: IPImageFit.centerContain,
  width: 128,
  height: 128,
};

const DashboardOverview = (props: IDashboardOverviewProps) => {
  const { t } = useTranslation(['translation', 'dashboard', 'activity']);
  const appContext = useContext(AppContext);
  const _sceneBaseURL = '/dashboard';
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dashboardScene, setDashboardScene] = useState<DashboardScene | undefined>(undefined);
  const history = useHistory();
  const isGenericManager = hasUserFeatureGenericManager(appContext);
  const isPortalSubscription = appContext.user.login.subscriptionType === SubscriptionTypes.Portal;
  const canShowPartnerDashboardPivot =
    appContext.user.login.isPartner && hasUserFeature(appContext, FeatureTypes.PartnerAdmin);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async (signal?: AbortSignal) => {
    if (isLoading) {
      return;
    }

    //we must check this because the dashboard is always loaded
    //and would otherwise generate and error
    if (!appContext.isAuthenticated || !appContext.user.login.userLicensed) {
      return;
    }

    if (!hasUserFeature(appContext, FeatureTypes.Dashboard)) {
      return;
    }

    try {
      setIsLoading(true);
      appContext.showContentLoader();

      //load dashboard data
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      if (signal?.aborted) return;

      const newDashboardScene = await apiGetDashboardScene(accessToken, appContext.globalDataCache, signal);
      if (signal?.aborted) return;

      //get all org units the user has access to from the login object
      //this is used to fill the shared data object
      newDashboardScene.userOrgUnits = appContext.user.login.tenants.filter((t) =>
        isSameDirectory(t, appContext.user.tenant),
      );

      setDashboardScene(newDashboardScene);
    } catch (err) {
      if (signal?.aborted) return;
      appContext.setError(err);
    } finally {
      if (signal?.aborted) return;
      appContext.hideContentLoader();
      setIsLoading(false);
    }
  };

  const getTrialMsg = () => {
    if (appContext.user.login.isInTrial() && !appContext.isMobileView) {
      const days = appContext.user.login.trialDaysLeft();
      if (days === 0) {
        return t('dashboard:Greeting.Trial0');
      } else if (days === 1) {
        return t('dashboard:Greeting.Trial1');
      } else {
        return t('dashboard:Greeting.Trial', { days: days });
      }
    }

    return '';
  };

  const getTitle = () => {
    let timeGreeting = t('dashboard:Greeting.GoodMorning');
    if (!isAm()) timeGreeting = t('dashboard:Greeting.GoodAfternoon');

    return `${timeGreeting}, ${appContext.user.name} ${getTrialMsg()}`;
  };

  //the set-up wizard is offered during the trial and, after a conversion, until its first run finished;
  //whether the menu is hidden for it is the server's gate (isOnboardingInProgress), never decided here
  const showOnboarding = appContext.user.login.isInTrial() || appContext.user.login.hasUnfinishedOnboarding;

  const getSubtitle = () => {
    if (props.location.pathname.toLowerCase() === `${_sceneBaseURL}/welcome`) {
      if (appContext.user.login.isInTrial()) return t('dashboard:TrialWelcome.SceneSubTitle');
      if (appContext.user.login.hasUnfinishedOnboarding) return t('dashboard:TrialWelcome.SceneSubTitleSetup');
    }

    return t('dashboard:SubTitle');
  };

  const getPivotItems = () => {
    const pivotItems: IScenePivotItemProps[] = [];

    if (appContext.user.login.tenantLicensed === false) {
      return pivotItems; //no tabs when tenant is not licensed
    }

    if (showOnboarding && isGenericManager) {
      //show the welcome tab:
      //1. as long as the trial period is active, or after a conversion until the set-up finished
      //2. user is manager/admin/consultant
      const pivotTrialWelcome = {
        key: 'trialwelcome',
        text: t('dashboard:Pivot.TrialWelcome'),
        url: '/welcome',
      };
      pivotItems.push(pivotTrialWelcome);
    }

    if (appContext.user.login.trialEnded() === false) {
      const pivotMe = { key: 'me', text: t('dashboard:Pivot.ForMe'), url: '/me' };
      pivotItems.push(pivotMe);

      const pivotItemsTeamOrg: IScenePivotItemProps[] = [
        { key: 'team', text: t('dashboard:Pivot.Team'), url: '/team' },
        { key: 'org', text: t('dashboard:Pivot.Org'), url: '/org' },
      ];
      pivotItems.push(...pivotItemsTeamOrg);

      if (canShowPartnerDashboardPivot) {
        pivotItems.push({ key: 'partner', text: t('dashboard:Pivot.Partner'), url: '/partner' });
      }
    }

    return pivotItems;
  };

  const getDefaultUrl = (): string => {
    //land on the wizard during the trial and while the menu is hidden for it; a converted organization lands on its
    //own dashboard and reaches the wizard through the Welcome tab
    const landOnWelcome = appContext.user.login.isInTrial() || appContext.isOnboardingInProgress === true;
    const def = landOnWelcome && isGenericManager ? 'welcome' : 'me';

    return `${_sceneBaseURL}/${def}`;
  };

  const gotoSubscription = () => {
    history.push('/admin/subscription');
  };

  //
  // Main render
  //

  if (isPortalSubscription) {
    return (
      <IPStack verticalFill styles={globalStackStylesPaddingScene} tokens={globalStackTokensGapLarge}>
        <IPStack.Item>
          <SceneBar
            title={getTitle()}
            subtitle={t('dashboard:Portal.SubTitle')}
            image="dashboard.png"
            pivotItems={[]}
            selectedPivotKey={getCurrentPivotKeyFromURL([])}
          />
        </IPStack.Item>
        <IPStack verticalFill verticalAlign="center" horizontalAlign="center" tokens={globalStackTokensGapSmall}>
          <IPText variant="xLarge">{t('dashboard:Portal.Title')}</IPText>
          <IPText variant="large">{t('dashboard:Portal.Message')}</IPText>
        </IPStack>
      </IPStack>
    );
  }

  //the license manager only see the scene bar
  if (!hasUserFeature(appContext, FeatureTypes.Dashboard)) {
    return (
      <IPStack verticalFill styles={globalStackStylesPaddingScene} tokens={globalStackTokensGapLarge}>
        <IPStack.Item>
          <SceneBar
            title={getTitle()}
            subtitle={getSubtitle()}
            image="dashboard.png"
            pivotItems={[]}
            selectedPivotKey={getCurrentPivotKeyFromURL(getPivotItems())}
          />
        </IPStack.Item>
        <IPStack verticalFill verticalAlign="center" horizontalAlign="center" tokens={globalStackTokensGapLarge}>
          <IPImage {...imagePropsLicenseManager} />
          <IPText variant="large">{t('dashboard:LicenseManagerInfo')}</IPText>
          <IPStack tokens={globalStackTokensGapSmall}>
            <LearnMore learnMoreLink={globalKB_AdminUsers} text={t('dashboard:LicenseManagerInfoLinkAdminUsers')} />
            <LearnMore
              learnMoreLink={globalKB_AdminSubscription}
              text={t('dashboard:LicenseManagerInfoLinkAdminSubscription')}
            />
          </IPStack>
        </IPStack>
      </IPStack>
    );
  }

  return (
    <IPStack horizontal styles={globalStackStylesHeight100}>
      <CopilotDashboardDeepLinkActivator />
      <IPStack.Item grow>
        <IPStack verticalFill styles={globalStackStylesHeight100} tokens={globalStackTokensGapLarge}>
          <IPStack.Item>
            <SceneBar
              title={getTitle()}
              subtitle={getSubtitle()}
              image="dashboard.png"
              pivotItems={appContext.isOnboardingInProgress ? [] : getPivotItems()}
              selectedPivotKey={getCurrentPivotKeyFromURL(getPivotItems())}
            />
          </IPStack.Item>
          {!isLoading && appContext.user.login.tenantLicensed === false && (
            <IPStack verticalFill tokens={globalStackTokensGapLarge} styles={globalStackItemStylesPaddingScene}>
              <IPStack.Item>
                <WarningMessage message={t('dashboard:NoLicense')}></WarningMessage>
              </IPStack.Item>
              <IPStack.Item>
                <IPPrimaryButton onClick={() => gotoSubscription()}>{t('dashboard:NoLicenseAction')}</IPPrimaryButton>
              </IPStack.Item>
            </IPStack>
          )}
          {appContext.user.login.tenantLicensed === true && (
            <IPStack.Item grow styles={{ root: { paddingLeft: globalPaddingScene, height: '50%' } }}>
              <Switch>
                <Route
                  path={`${_sceneBaseURL}/me`}
                  render={() => (
                    <DashboardCollection
                      dashboardScene={dashboardScene}
                      dashboardType={DashboardTypes.user}
                      onRefreshSharedData={() => loadData()}
                    />
                  )}
                />
                <Route
                  path={`${_sceneBaseURL}/team`}
                  render={() => (
                    <DashboardCollection
                      dashboardScene={dashboardScene}
                      dashboardType={DashboardTypes.team}
                      onRefreshSharedData={() => loadData()}
                    />
                  )}
                />
                <Route
                  path={`${_sceneBaseURL}/org`}
                  render={() => (
                    <DashboardCollection
                      dashboardScene={dashboardScene}
                      dashboardType={DashboardTypes.org}
                      onRefreshSharedData={() => loadData()}
                    />
                  )}
                />
                <Route
                  path={`${_sceneBaseURL}/partner`}
                  render={() =>
                    canShowPartnerDashboardPivot ? <PartnerDashboardContent /> : <Redirect to={`${_sceneBaseURL}/me`} />
                  }
                />
                <Route
                  path={`${_sceneBaseURL}/welcome`}
                  exact
                  render={() => (isGenericManager ? <TrialWelcome /> : <Redirect to={`${_sceneBaseURL}/me`} />)}
                />
                <Redirect from={`${_sceneBaseURL}`} to={getDefaultUrl()}></Redirect>
                <Redirect to={globalNotFoundURL}></Redirect>
              </Switch>
            </IPStack.Item>
          )}
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};

export default DashboardOverview;
